import { AppProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AuthProvider } from '@/auth/AuthProvider';
import { LoginPage } from '@/auth/LoginPage';
import { RequireAuth } from '@/auth/RequireAuth';
import { EditorPage } from '@/shell/EditorPage';
import { DashboardHome } from '@/shell/DashboardHome';
import { UsersPage } from '@/shell/UsersPage';
import { ApprovalsPage } from '@/shell/ApprovalsPage';
import { PushNotificationsPage } from '@/shell/PushNotificationsPage';
import { CommsOrdersPage } from '@/shell/CommsOrdersPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount: number, err: unknown) => {
        if (err && typeof err === 'object' && 'code' in err) {
          const code = (err as { code?: string }).code;
          if (code === 'unauthorized' || code === 'forbidden') return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

// Polaris's AppProvider needs a `Link` integration that uses the SPA router
// so internal navigation doesn't trigger a full reload.
function AppProviderWithRouter({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const linkComponent = useCallback(
    ({
      url,
      children,
      external,
      target: _target,
      ref: _ref,
      ...rest
    }: {
      url: string;
      children?: React.ReactNode;
      external?: boolean;
      target?: string;
      ref?: unknown;
    } & React.HTMLAttributes<HTMLAnchorElement>) => {
      if (external || /^(https?:)?\/\//.test(url) || url.startsWith('mailto:')) {
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" {...rest}>
            {children}
          </a>
        );
      }
      return (
        <a
          href={url}
          onClick={(e) => {
            e.preventDefault();
            navigate(url);
          }}
          {...rest}
        >
          {children}
        </a>
      );
    },
    [navigate],
  );

  return (
    <AppProvider i18n={enTranslations} linkComponent={linkComponent}>
      {children}
    </AppProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        basename={
          import.meta.env.DEV
            ? import.meta.env.BASE_URL.replace(/\/$/, '')
            : ''
        }
      >
        <AppProviderWithRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <EditorPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <DashboardHome />
                  </RequireAuth>
                }
              />
              <Route
                path="/users"
                element={
                  <RequireAuth>
                    <UsersPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/approvals"
                element={
                  <RequireAuth>
                    <ApprovalsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/push-notifications"
                element={
                  <RequireAuth>
                    <PushNotificationsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/comms/orders"
                element={
                  <RequireAuth>
                    <CommsOrdersPage />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </AppProviderWithRouter>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
